import { useState, useEffect } from "react";
import { X, FileEdit, Loader2, Leaf, Droplets, Thermometer, Mountain, Layers, Info, MapPin, Shield, AlertTriangle, Star, StarOff, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { authFetch } from "@/lib/api";
import ModificationForm from "@/components/ModificationForm";
import ParcelLockPanel from "@/components/ParcelLockPanel";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InfoRow = ({ icon: Icon, label, value, color = '#2D6A4F', sublabel }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-black/5 last:border-0">
    <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color }} />
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#4A4A4A' }}>{label}</p>
      <p className="text-sm font-medium" style={{ color: '#1A1A1A' }}>{value}</p>
      {sublabel && (
        <p className="text-[10px] mt-0.5 italic" style={{ color: '#7A7A7A' }}>{sublabel}</p>
      )}
    </div>
  </div>
);

const AgroRow = ({ icon: Icon, label, data, color = '#2D6A4F' }) => {
  if (!data || !data.valeur) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-black/5 last:border-0">
      <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color }} />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#4A4A4A' }}>{label}</p>
        <p className="text-sm font-medium" style={{ color: '#1A1A1A' }}>{data.valeur}{typeof data.valeur === 'number' ? '' : ''}</p>
        <p className="text-[9px] mt-0.5" style={{ color: '#9A9A9A' }}>
          Source: {data.source}
        </p>
        <p className="text-[9px]" style={{ color: '#AAAAAA' }}>
          {data.methode} (fiabilite: {data.fiabilite})
        </p>
      </div>
    </div>
  );
};

// ==================== Custom Agro Data Tab ====================
// Références cadastrales recouvertes par la parcelle (La Réunion)
function CadastreRefs({ parcel }) {
  const [refs, setRefs] = useState(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const isReunion = String(parcel?.commune_code || parcel?.parcel_id || "").includes("974");
  useEffect(() => {
    if (!parcel?.parcel_id || !isReunion) return;
    let off = false;
    setRefs(null); setFailed(false);
    // Une nouvelle tentative automatique (réseau lent, serveur qui redémarre), puis bouton « Réessayer »
    const load = (left) => authFetch(`${API_BASE}/cadastre/parcel/${encodeURIComponent(parcel.parcel_id)}`)
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then((d) => { if (!off) setRefs(d.refs || []); })
      .catch(() => {
        if (off) return;
        if (left > 0) setTimeout(() => { if (!off) load(left - 1); }, 3000);
        else { setFailed(true); setRefs([]); }
      });
    load(1);
    return () => { off = true; };
  }, [parcel?.parcel_id, isReunion, attempt]);
  if (!isReunion) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-black/5" data-testid="parcel-cadastre-refs">
      <MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#E07A5F" }} />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Références cadastrales</p>
        {refs === null ? <Loader2 className="w-3.5 h-3.5 animate-spin mt-1" style={{ color: "#E07A5F" }} /> : refs.length === 0 ? (
          failed ? (
            <button onClick={() => setAttempt((a) => a + 1)} className="text-xs underline" style={{ color: "#B4532F" }} data-testid="parcel-cadastre-retry">
              Chargement impossible · Réessayer
            </button>
          ) : <p className="text-xs" style={{ color: "#7A7A7A" }}>Non disponibles</p>
        ) : (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {refs.slice(0, 12).map((c) => (
              <span key={c.idu} className="font-mono text-[11px] rounded px-1.5 py-0.5" style={{ background: "#FBEDE8", color: "#B4532F" }}
                title={`${c.idu} · ${c.surface_ha} ha${c.share_of_parcel_pct != null ? ` · ${c.share_of_parcel_pct} % de la parcelle` : ""}`}>
                {String(c.section).replace(/^0/, "")} {parseInt(c.numero, 10)}
                {refs.length > 1 && c.share_of_parcel_pct != null && <span className="opacity-70"> · {c.share_of_parcel_pct} %</span>}
              </span>
            ))}
            {refs.length > 12 && <span className="text-[11px]" style={{ color: "#7A7A7A" }}>+{refs.length - 12}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

export const ParcelDetail = ({ parcel, onClose, onRefresh, user, onPhyto }) => {
  const [showModForm, setShowModForm] = useState(false);
  // Suppression d'une parcelle tracée à la main : son créateur ou l'admin (le serveur vérifie aussi)
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [isFollowed, setIsFollowed] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [canAccessCarnet, setCanAccessCarnet] = useState(false);

  useEffect(() => {
    if (parcel?.parcel_id) {
      authFetch(`${API_BASE}/parcels/${parcel.parcel_id}/is-followed`)
        .then(r => r.json())
        .then(d => setIsFollowed(d.followed))
        .catch(() => {});
    }
  }, [parcel?.parcel_id]);

  const toggleFollow = async () => {
    setFollowLoading(true);
    try {
      const method = isFollowed ? "DELETE" : "POST";
      const res = await authFetch(`${API_BASE}/parcels/${parcel.parcel_id}/follow`, { method });
      if (res.ok) {
        setIsFollowed(!isFollowed);
        toast.success(isFollowed ? "Parcelle retiree du suivi" : "Parcelle ajoutee au suivi");
        // La carte recolore les parcelles suivies (MapDashboard écoute cet événement)
        window.dispatchEvent(new CustomEvent("veyer:follow-changed", {
          detail: { parcelId: parcel.parcel_id, followed: !isFollowed },
        }));
      }
    } catch { toast.error("Erreur"); }
    setFollowLoading(false);
  };

  const agro = parcel.agronomic_data || {};
  const isRPG = parcel.detection_method === "rpg_officiel";

  const statusLabel = {
    active: "Actif",
    pending_modification: "Modification en cours",
    archived: "Archive"
  };
  const statusColor = {
    active: "#2D6A4F",
    pending_modification: "#E9C46A",
    archived: "#4A4A4A"
  };

  const isManual = parcel.detection_method === "saisie_manuelle";
  const methodLabel = isRPG ? "RPG IGN" : isManual ? "Saisie manuelle" : "Detection IA";
  const methodColor = isRPG ? "#2D6A4F" : "#E07A5F";

  const canDelete = isManual && !!user && (parcel.created_by === user.user_id || user.is_admin)
    && (!parcel.locked_by || parcel.locked_by === user.user_id || user.is_admin);
  const deleteParcel = async () => {
    setDeleting(true);
    try {
      const res = await authFetch(`${API_BASE}/parcels/${encodeURIComponent(parcel.parcel_id)}`, { method: "DELETE" });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Suppression impossible");
      toast.success(`Parcelle « ${parcel.name} » supprimée`);
      onClose?.();
      onRefresh?.();
    } catch (e) {
      toast.error(e.message);
      setDeleting(false);
    }
  };

  return (
    <div className="floating-panel h-full flex flex-col overflow-hidden" data-testid="parcel-detail">
      {/* Header */}
      <div className="p-4 border-b border-black/5">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold tracking-tight truncate" style={{ fontFamily: 'Manrope, sans-serif', color: '#1A1A1A' }}>
              {parcel.name}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: '#4A4A4A' }}>
              {parcel.commune}
              {parcel.cirque && <span className="ml-1 font-medium" style={{ color: '#8B5E3C' }}> ({parcel.cirque})</span>}
            </p>
          </div>
          <Button
            data-testid="close-detail-btn"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          <Badge
            data-testid="detection-method-badge"
            className="text-[10px] font-semibold border-0"
            style={{ background: methodColor + '15', color: methodColor }}
          >
            <Shield className="w-3 h-3 mr-1" />
            {methodLabel}
          </Badge>
          <Badge
            className="text-[10px] font-medium border-0"
            style={{ background: statusColor[parcel.status] + '20', color: statusColor[parcel.status] }}
          >
            {statusLabel[parcel.status] || parcel.status}
          </Badge>
          <Badge variant="outline" className="text-[10px] font-medium">
            {parcel.surface_ha} ha
          </Badge>
        </div>
        {/* Follow button */}
        <Button size="sm" variant={isFollowed ? "default" : "outline"}
          className={`w-full h-8 text-[11px] font-semibold rounded-lg gap-1.5 mt-3 ${isFollowed ? 'text-white' : 'border-black/10'}`}
          style={isFollowed ? { background: "#E07A5F" } : { color: "#2D6A4F" }}
          onClick={toggleFollow} disabled={followLoading} data-testid="follow-parcel-btn">
          {followLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isFollowed ? <StarOff className="w-3.5 h-3.5" /> : <Star className="w-3.5 h-3.5" />}
          {isFollowed ? "Retirer du suivi" : "Suivre cette parcelle"}
        </Button>

        {/* Lock panel — Phase 3 (verrouillage + gestion accès) */}
        <div className="mt-3">
          <ParcelLockPanel parcelId={parcel.parcel_id} onAccessChanged={setCanAccessCarnet} />
        </div>

        {/* Le serveur décide : une parcelle verrouillée par un autre compte reste fermée, même pour un Pro */}
        {canAccessCarnet && onPhyto && (
          <Button size="sm" className="w-full h-8 text-[11px] font-semibold rounded-lg text-white gap-1.5 mt-2" style={{ background: "#2D6A4F" }}
            onClick={() => onPhyto(parcel)} data-testid="open-carnet-btn">
            <Leaf className="w-3.5 h-3.5" /> Carnet de Parcelle PRO
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="px-4 pt-2 bg-transparent justify-start gap-1 shrink-0 flex-wrap">
          <TabsTrigger value="details" className="text-xs h-auto py-1.5 px-3 rounded-full" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="agro" className="text-xs h-auto py-1.5 px-3 rounded-full" data-testid="tab-agro">Agronomie</TabsTrigger>
        </TabsList>

        <ScrollArea className="flex-1">
          <TabsContent value="details" className="p-4 mt-0 space-y-1">
            <InfoRow icon={Leaf} label="Culture" value={parcel.culture_type} />
            {(parcel.growing_medium || parcel.greenhouse != null) && (
              <InfoRow icon={Layers} label="Type de parcelle" value={[
                parcel.growing_medium === "hors_sol" ? "Hors sol" : parcel.growing_medium === "sol" ? "En sol" : null,
                parcel.greenhouse == null ? null : parcel.greenhouse ? "sous serre" : "plein champ",
              ].filter(Boolean).join(" · ")} />
            )}
            <InfoRow icon={MapPin} label="Commune" value={parcel.commune} />
            <CadastreRefs parcel={parcel} />
            {parcel.cirque && (
              <InfoRow icon={Mountain} label="Cirque" value={parcel.cirque} color="#8B5E3C" />
            )}
            <InfoRow icon={Layers} label="Surface" value={`${parcel.surface_ha} hectares`} sublabel={isManual ? "Calculée sur le tracé" : `Source: RPG IGN (surf_parc)`} />
            <InfoRow
              icon={Shield}
              label="Methode de detection"
              value={isRPG ? "Registre Parcellaire Graphique (officiel)" : isManual ? "Saisie manuelle" : "Detection IA"}
              sublabel={parcel.detection_source || ""}
              color={methodColor}
            />
            <InfoRow icon={Sparkles} label="Confiance detection" value={`${Math.round((parcel.detection_confidence || 0) * 100)}%`} color="#E07A5F" />
            <InfoRow icon={Info} label="Derniere mise a jour" value={
              parcel.last_detection ? new Date(parcel.last_detection).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'
            } />
          </TabsContent>

          <TabsContent value="agro" className="p-4 mt-0 space-y-0">
            {agro.avertissement && (
              <div className="flex items-start gap-2 p-3 mb-3 rounded-lg border" style={{ borderColor: '#E9C46A40', background: '#E9C46A08' }}>
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#E9C46A' }} />
                <p className="text-[10px] leading-relaxed" style={{ color: '#6B6B3B' }}>
                  {agro.avertissement}
                </p>
              </div>
            )}

            <AgroRow icon={Mountain} label="Altitude" data={
              agro.altitude_m ? { ...agro.altitude_m, valeur: `${agro.altitude_m.valeur} m` } : null
            } />
            <AgroRow icon={Droplets} label="Pluviometrie annuelle" data={
              agro.pluviometrie_mm_an ? { ...agro.pluviometrie_mm_an, valeur: `${agro.pluviometrie_mm_an.valeur} mm/an` } : null
            } />
            <AgroRow icon={Thermometer} label="Temperature moyenne" data={
              agro.temperature_moy_c ? { ...agro.temperature_moy_c, valeur: `${agro.temperature_moy_c.valeur} °C` } : null
            } color="#E07A5F" />
            <AgroRow icon={Layers} label="Type de sol" data={agro.type_sol} color="#8B5E3C" />
            <AgroRow icon={Leaf} label="Zone climatique" data={agro.zone_climatique} />

            {agro.type_sol && !agro.type_sol.valeur && (
              <>
                <InfoRow icon={Layers} label="Type de sol" value={agro.type_sol} />
                <InfoRow icon={Mountain} label="Altitude" value={agro.altitude_m ? `${agro.altitude_m} m` : 'N/A'} />
                <InfoRow icon={Droplets} label="Pluviometrie" value={agro.pluviometrie_mm ? `${agro.pluviometrie_mm} mm` : 'N/A'} />
                <InfoRow icon={Thermometer} label="Temperature" value={agro.temperature_moy_c ? `${agro.temperature_moy_c} °C` : 'N/A'} />
              </>
            )}
          </TabsContent>
        </ScrollArea>
      </Tabs>

      {/* Actions */}
      <div className="p-4 border-t border-black/5 space-y-2">
        <Button
          data-testid="modify-btn"
          variant="outline"
          className="w-full h-9 rounded-full text-xs font-semibold border-black/10"
          onClick={() => setShowModForm(true)}
        >
          <FileEdit className="w-3.5 h-3.5 mr-1.5" />
          Demander une modification
        </Button>
        {canDelete && !confirmDelete && (
          <Button data-testid="delete-manual-parcel-btn" variant="ghost" className="w-full h-8 rounded-full text-xs font-semibold"
            style={{ color: "#B42318" }} onClick={() => setConfirmDelete(true)}>
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Supprimer cette parcelle
          </Button>
        )}
        {canDelete && confirmDelete && (
          <div className="rounded-xl p-3 space-y-2 text-xs" style={{ background: "#FEF3F2", border: "1px solid #FDA29B" }} data-testid="delete-manual-parcel-confirm">
            <p style={{ color: "#7A271A" }}>
              Supprimer définitivement « {parcel.name} » ? Elle disparaît de la carte et des parcelles suivies. Cette action est irréversible.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 h-8 rounded-full text-xs" onClick={() => setConfirmDelete(false)} disabled={deleting}>Annuler</Button>
              <Button className="flex-1 h-8 rounded-full text-xs text-white" style={{ background: "#B42318" }} onClick={deleteParcel} disabled={deleting} data-testid="delete-manual-parcel-confirm-btn">
                {deleting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}Supprimer
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modification Form Dialog */}
      {showModForm && (
        <ModificationForm
          parcel={parcel}
          onClose={() => setShowModForm(false)}
          onSuccess={() => {
            setShowModForm(false);
            onRefresh();
            toast.success("Demande de modification soumise");
          }}
        />
      )}
    </div>
  );
};

export default ParcelDetail;
